import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageWrapper from "../pageWrapper";
import { fetchWithTimeout } from "../../hooks/useFetchWithTimeout";

//CSS
import "./itemCreationPage.css";
import "../0PageStyles.css";

//Components
import InputField from "../../Components/Other Components/inputField";
import InputGroup from "../../Components/Other Components/inputGroup";
import Button from "../../Components/Other Components/button";
import TagPicker from "../../Components/Tag Components/tagPicker";
import ClientPicker from "../../Components/Client Components/clientPicker";
import PageLoader from "../../Components/Other Components/pageLoader";
import MandatoryExplanation from "../../Components/Other Components/mandatoryExplanation";
import BarcodeScannerComponent from "../../Components/Other Components/BarcodeScannerComponent";

//Functions and hooks
import { CreateItem, GetNextItemID } from "../../BackendAccess/ItemHooks";
import { GetUnits } from "../../BackendAccess/UnitHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import useNarrowMode from "../../hooks/useNarrowModeChecker";
import { useSettings, getCurrencySymbol } from "../../Contexts/SettingsContext";
import { useTranslation } from "react-i18next";

function ItemCreationPage() {
  const [ID, setID] = useState();
  const [Barcode, setBarcode] = useState();

  const [Name, setName] = useState();
  const [Unit, setUnit] = useState();
  const [Price, setPrice] = useState(0);
  const [SellingPrice, setSellingPrice] = useState(0);
  const [MinStock, setMinStock] = useState("");
  const [Tags, setTags] = useState([]);
  const [Clients, setClients] = useState([]);
  const [Units, setUnits] = useState([]);

  const [scanning, setScanning] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(false);

  const [createBtnDisabled, setCreateBtnDisabled] = useState(false);

  const navigate = useNavigate();

  const { showNotification } = useNotification();
  useErrorHandler(error, showNotification, setPageError);

  const isNarrow = useNarrowMode();

  const { companySettings, refreshCompanySettings } = useSettings();

  const { t } = useTranslation();

  useEffect(() => {
    setLoading(true);
    const fetchInitialValues = async () => {
      await refreshCompanySettings();
      const [data, error] = await fetchWithTimeout(GetNextItemID);
      if (error) {
        error.isPageError = true;
        setError(error);
      } else {
        setID(data);
      }
      const [units, countError] = await fetchWithTimeout(GetUnits);
      if (countError) {
        countError.isPageError = true;
        setError(countError);
      } else {
        setUnits(
          units.map((item) => {
            return { value: item.UnitID, label: item.Name };
          })
        );
      }

      setLoading(false);
    };
    fetchInitialValues();
  }, []);

  const handleIDChange = (e) => {
    const value = e;

    setID(value);
  };
  const handleBarcodeChange = (e) => {
    const value = e;
    setBarcode(value);
  };
  const handleNameChange = (e) => {
    const value = e;
    setName(value);
  };
  const handleUnitChange = (e) => {
    const value = e;
    setUnit(value);
  };
  const handlePriceChange = (e) => {
    let value = e;

    if (/^[0-9]*\.?[0-9]{0,2}$/.test(value)) {
      setPrice(value);
    } else {
      setError({
        err: t(
          "validation.Price can only be a number, and have 2 decimal digits.",
          { ns: "error" }
        ),
        errCode: 400,
      });
    }
  };
  const handleSellingPriceChange = (e) => {
    let value = e;

    if (/^[0-9]*\.?[0-9]{0,2}$/.test(value)) {
      setSellingPrice(value);
    } else {
      setError({
        err: t(
          "validation.Price can only be a number, and have 2 decimal digits.",
          { ns: "error" }
        ),
        errCode: 400,
      });
    }
  };
  const handleMinStockChange = (e) => {
    const value = e;
    if (value === "" || /^[0-9]*$/.test(value)) {
      setMinStock(value);
    }
  };

  const OnCreate = async () => {
    setCreateBtnDisabled(true);
    showNotification(t("Creating item...", { ns: "items" }), "info");
    try {
      const [data, error] = await CreateItem(
        ID,
        Barcode,
        Name,
        Unit,
        Price,
        SellingPrice,
        Tags.toString(),
        Clients.toString(),
        MinStock
      );
      await createNItems(0);
      if (error) {
        setError(error);
      } else {
        showNotification(
          t("Item created successfully!", { ns: "items" }),
          "success"
        );
        navigate(-1);
      }
    } catch (err) {
      setError({
        err: t("server:Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setCreateBtnDisabled(false);
    }
  };

  const createNItems = async (n) => {
    for (let i = 1; i < n; i++) {
      const [data, error] = await CreateItem(
        ID + i,
        "",
        "Item " + (ID + i).toString(),
        "kg",
        i * 100,
        Tags.toString(),
        Clients.toString()
      );

      if (error) {
        setError(error);

        return;
      }
    }
    if (n > 0)
      showNotification("Created " + n + " items successfully!", "success");
  };
  const clickOnCancel = () => {
    showNotification(t("Item creation canceled.", { ns: "items" }), "info");
    navigate(-1);
  };

  const onSelectedTagsChanged = (tags) => {
    setTags(tags);
  };

  const onSelectedClientsChanged = (clients) => {
    setClients(clients);
  };

  const tutorialSteps = [
    {
      target: null,
      title: t("Creating a new item", { ns: "tutorial" }),
      message: t(
        "This page adds a new item to your inventory catalog. We'll walk through every field so you know exactly what to fill in. Required fields are marked with a red *.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-creation-id",
      title: t("Item ID", { ns: "tutorial" }),
      message: t(
        "This is where the item's ID goes, in the format ITM-NNNLL-NNN (numbers and letters). It's auto-filled with the next available ID for you, but you can change it to anything you like - it just has to be unique for every item.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-creation-barcode",
      title: t("Barcode", { ns: "tutorial" }),
      message: t(
        "Optional. Enter a barcode number, a link, or any custom text here - whatever you'd scan or type to identify this item at checkout or on a shipping label.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-creation-scan-barcode",
      title: t("Scan instead of typing", { ns: "tutorial" }),
      message: t(
        "Rather than typing the barcode by hand, you can tap here to scan it with your device's camera.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-creation-name",
      title: t("Name", { ns: "tutorial" }),
      message: t(
        "The item's name, as you'll see it everywhere in the app - item lists, traffic notes, reports. Make it clear enough to recognize at a glance.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-creation-unit",
      title: t("Unit", { ns: "tutorial" }),
      message: t(
        "Choose how this item is measured and counted - for example pieces, kilograms, or liters. You can manage the list of available units from the Units page.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-creation-price",
      title: t("Price", { ns: "tutorial" }),
      message: t(
        "The price you pay to acquire this item - its purchase price. Shown in your company's configured currency.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-creation-selling-price",
      title: t("Selling price", { ns: "tutorial" }),
      message: t(
        "The price you sell this item for. This is used to calculate profit margins in the inventory report.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-creation-min-stock",
      title: t("Minimum stock", { ns: "tutorial" }),
      message: t(
        "Optional. Set a quantity threshold - once this item's total stock across all warehouses drops below it, it'll show up on the Low Stock page as a warning.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-creation-tags",
      title: t("Tags", { ns: "tutorial" }),
      message: t(
        "Tags help you group and filter items later - for example by category, storage requirements, or fragility. Pick existing tags or create a new one right here.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-creation-suppliers",
      title: t("Suppliers", { ns: "tutorial" }),
      message: t(
        "Optionally link this item to the supplier or manufacturer you source it from, so you can track where your stock comes from.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-creation-create-btn",
      title: t("Save the item", { ns: "tutorial" }),
      message: t(
        "Once everything looks right, click here to create the item. It'll then show up in your Items list, ready to be stocked into a warehouse.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="itemCreation" tutorialSteps={tutorialSteps}>
      <PageLoader loading={loading} error={pageError}>
        <div className="page-content">
          <div className="container-bordered item-creation w-90 vertical mb-5 mt-4  ">
          <InputGroup className="w-100">
            <InputField
              placeholder={t("Example: 1001")}
              label={t("ID") + ":"}
              value={ID}
              onChange={handleIDChange}
              className="mb-2"
              mandatory={true}
              readOnly={true}
              tutorialId="item-creation-id"
            />
            <InputField
              placeholder={t("Example: 1234542,some-link.com,custom text", {
                ns: "items",
              })}
              label={t("Barcode") + ":"}
              value={Barcode}
              onChange={handleBarcodeChange}
              className="mb-2"
              description={t("Barcode or custom qr code", {
                ns: "items",
              })}
              tutorialId="item-creation-barcode"
            />

            <div data-tutorial-id="item-creation-scan-barcode">
              <BarcodeScannerComponent
                onScanSuccess={(code) => setBarcode(code)}
              />
            </div>

            <InputField
              placeholder={t("Example: Small bolt", { ns: "items" })}
              label={t("Name") + ":"}
              value={Name}
              onChange={handleNameChange}
              className="mb-2"
              mandatory={true}
              tutorialId="item-creation-name"
            />
            <InputField
              placeholder={t("Example: kg", { ns: "items" })}
              label={t("Unit") + ":"}
              value={Unit}
              onChange={handleUnitChange}
              type="dropdown"
              options={Units}
              className="mb-2"
              mandatory={true}
              tutorialId="item-creation-unit"
            />
            <InputField
              placeholder={t("Example: 130.20", { ns: "items" })}
              label={
                t("Price") +
                ": (" +
                getCurrencySymbol(companySettings.currency) +
                ")"
              }
              value={Price}
              onChange={handlePriceChange}
              className="mb-2"
              mandatory={true}
              tutorialId="item-creation-price"
            />
            <InputField
              placeholder={t("Example: 130.20", { ns: "items" })}
              label={
                t("Selling Price") +
                ": (" +
                getCurrencySymbol(companySettings.currency) +
                ")"
              }
              value={SellingPrice}
              onChange={handleSellingPriceChange}
              className="mb-2"
              mandatory={true}
              tutorialId="item-creation-selling-price"
            />
            <InputField
              placeholder={t("Example: 10", { ns: "items" })}
              label={t("Minimum Stock", { ns: "items" }) + ":"}
              value={MinStock}
              onChange={handleMinStockChange}
              className="mb-2"
              description={t(
                "Get a low-stock warning once total quantity across all warehouses falls below this",
                { ns: "items" }
              )}
              tutorialId="item-creation-min-stock"
            />
            <MandatoryExplanation />
          </InputGroup>
          </div>
          <div className="container-bordered item-creation horizontal mb-2 w-90 ">
            <div
              data-tutorial-id="item-creation-tags"
              className={isNarrow ? "mb-4 w-100" : "mr-4 w-100"}
            >
              <TagPicker
                onChange={onSelectedTagsChanged}
                initSelectedTags={Tags}
              ></TagPicker>
            </div>
            <div
              data-tutorial-id="item-creation-suppliers"
              className={isNarrow ? "w-100" : "ml-4 w-100"}
            >
              <ClientPicker
                title={t("Suppliers")}
                onChange={onSelectedClientsChanged}
                typesToShow={["Manufacturer", "Supplier"]}
                PickedClients={Clients}
              ></ClientPicker>
            </div>
          </div>

          <div className="btn-container">
            <Button
              btnMargin={"btn-default"}
              btnStyle="btn-positive"
              onClick={OnCreate}
              disabled={createBtnDisabled}
              acceptEnter={true}
              icon={"create"}
              tutorialId="item-creation-create-btn"
            >
              {t("Create")}
            </Button>
            <Button
              btnMargin={"btn-default"}
              btnStyle="btn-primary"
              onClick={clickOnCancel}
            >
              {t("Cancel")}
            </Button>
          </div>
        </div>
      </PageLoader>
    </PageWrapper>
  );
}

export default ItemCreationPage;
